import { ref } from 'vue';
import SalesTransactionDocumentsAPI from 'dashboard/api/salesTransactionDocuments';
import SalesPropertyLookupsAPI from 'dashboard/api/salesPropertyLookups';
import { useSalesPropertyLookup } from './useSalesPropertyLookup';
import { useSalesOnusAnalysis } from './useSalesOnusAnalysis';

const idempotencyKey = () =>
  window.crypto?.randomUUID?.() || `sales-property:${Date.now()}`;

export function useSalesCreation({ router, route, t, notify }) {
  const lookup = useSalesPropertyLookup({ t });
  const onus = useSalesOnusAnalysis({ t, notify });
  const creating = ref(false);
  const confirmationKey = ref('');

  const updateCode = value => {
    confirmationKey.value = '';
    return lookup.search(value);
  };

  const uploadOnusDocument = async transactionId => {
    if (!onus.enabled.value || !onus.file.value) return true;
    let upload;
    try {
      upload = await SalesTransactionDocumentsAPI.upload(
        transactionId,
        onus.file.value
      );
    } catch (requestError) {
      onus.failUpload();
      notify(requestError?.response?.data?.message || onus.message.value);
      return false;
    }

    if (!onus.token.value) return true;
    try {
      const analysis = await SalesTransactionDocumentsAPI.analyzeOnus(
        transactionId,
        upload?.data?.data?.id,
        onus.token.value
      );
      const result = analysis?.data?.data || {};
      onus.applyResult(result.status);
      if (result.status === 'success') return true;

      notify(onus.message.value);
      return false;
    } catch (requestError) {
      onus.applyResult('unavailable');
      notify(requestError?.response?.data?.message || onus.message.value);
      return false;
    }
  };

  const create = async () => {
    if (!lookup.canConfirm.value || onus.processing.value) return null;

    creating.value = true;
    lookup.error.value = '';
    confirmationKey.value ||= idempotencyKey();
    try {
      const response = await SalesPropertyLookupsAPI.confirm({
        code: lookup.code.value,
        idempotency_key: confirmationKey.value,
      });
      const transaction = response?.data?.data || response?.data?.transaction;
      const transactionId = transaction?.id;

      if (!transactionId) {
        throw new Error('transaction_not_created');
      }

      await uploadOnusDocument(transactionId);
      await router.push({
        name: 'operacao_imobiliaria_vendas_cadastro',
        params: {
          accountId: route.params.accountId,
          salesTransactionId: transactionId,
        },
      });
      return transaction;
    } catch (requestError) {
      const message =
        requestError?.response?.data?.message ||
        requestError?.response?.data?.error ||
        t('SALES.CREATE.CREATE_ERROR');
      lookup.error.value = message;
      notify(message);
      return null;
    } finally {
      creating.value = false;
    }
  };

  return {
    canConfirm: lookup.canConfirm,
    code: lookup.code,
    create,
    creating,
    error: lookup.error,
    listing: lookup.listing,
    loading: lookup.loading,
    onusAnalysisMessage: onus.message,
    onusAnalysisProgress: onus.progress,
    onusAnalysisStatus: onus.status,
    onusEnabled: onus.enabled,
    onusFile: onus.file,
    onusProcessing: onus.processing,
    property: lookup.property,
    resetSelection: lookup.reset,
    search: lookup.search,
    setOnusEnabled: onus.setEnabled,
    setOnusFile: onus.setFile,
    source: lookup.source,
    updateCode,
  };
}
